import React, { useState, useEffect, useRef } from "react"
import { X, Send } from "lucide-react"
import { db } from "../../services/firebase"
import {
  collection,
  addDoc,
  serverTimestamp,
  doc,
  setDoc,
  onSnapshot,
} from "firebase/firestore"

// Typing indicator component
const TypingIndicator = ({ isTyping }) => {
  if (!isTyping) return null

  return (
    <div className="flex gap-3 mb-2">
      <div className="flex-shrink-0">
        <div className="w-8 h-8 rounded-full bg-green-600 flex items-center justify-center text-white text-xs font-medium">
          C
        </div>
      </div>
      <div className="flex-1 flex flex-col">
        <div className="max-w-[90%] rounded-2xl rounded-tl-sm px-4 py-3 bg-[#252525]">
          <div className="flex gap-1">
            <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" />
            <div
              className="w-2 h-2 bg-gray-500 rounded-full animate-bounce"
              style={{ animationDelay: "0.15s" }}
            />
            <div
              className="w-2 h-2 bg-gray-500 rounded-full animate-bounce"
              style={{ animationDelay: "0.3s" }}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

// Empty chat state
const EmptyChatState = () => {
  return (
    <div className="flex flex-col items-center justify-center h-full">
      <p className="text-gray-500 text-sm">No messages yet</p>
    </div>
  )
}

// Message bubble component
const MessageBubble = ({ message, isOwnMessage }) => {
  // Format timestamp if it exists
  let formattedTime = ""

  if (message.timestamp && message.timestamp.toDate) {
    try {
      // Firestore timestamp needs conversion to Date
      const date = message.timestamp.toDate()
      formattedTime = new Intl.DateTimeFormat("en-US", {
        hour: "numeric",
        minute: "numeric",
        hour12: true,
      }).format(date)
    } catch (error) {
      console.error("Error formatting timestamp:", error)
    }
  }

  return (
    <div className="flex flex-col mb-2">
      {isOwnMessage ? (
        // User message (right side)
        <div className="flex flex-col items-end">
          <span className="text-xs text-gray-500 mb-1.5">You</span>
          <div className="max-w-[80%] rounded-2xl rounded-tr-sm px-4 py-3 bg-blue-600 text-white">
            <p className="text-sm leading-relaxed">{message.text}</p>
          </div>
          {formattedTime && (
            <span className="text-xs text-gray-600 mt-1.5">
              {formattedTime}
            </span>
          )}
        </div>
      ) : (
        // Candidate message (left side)
        <div className="flex gap-3">
          {/* Candidate Avatar */}
          <div className="flex-shrink-0">
            <div className="w-8 h-8 rounded-full bg-green-600 flex items-center justify-center text-white text-xs font-medium">
              C
            </div>
          </div>

          {/* Message content */}
          <div className="flex-1 flex flex-col">
            <div className="max-w-[90%] rounded-2xl rounded-tl-sm px-4 py-3 bg-[#252525]">
              <p className="text-sm leading-relaxed text-gray-200">
                {message.text}
              </p>
            </div>
            {formattedTime && (
              <span className="text-xs text-gray-600 mt-1.5">
                {formattedTime}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

const CandidateChatSidebar = ({
  isOpen,
  onClose,
  meetingId,
  userId,
  messages = [],
}) => {
  const [message, setMessage] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [isInterviewerTyping, setIsInterviewerTyping] = useState(false)
  const messagesEndRef = useRef(null)
  const typingTimeoutRef = useRef(null)
  const messagesContainerRef = useRef(null)

  // Auto-scroll to the bottom when new messages are added
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" })
    }
  }, [messages])

  // Listen for typing indicator only (messages come from props now)
  useEffect(() => {
    if (!meetingId) return

    const cleanup = () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current)
      }
    }

    try {
      // Listen for typing indicator
      const typingRef = doc(db, "rooms", meetingId, "typing", "status")
      const typingUnsubscribe = onSnapshot(
        typingRef,
        (doc) => {
          if (doc.exists()) {
            const typingData = doc.data()

            // Only show typing indicator for other users
            if (typingData.userId && typingData.userId !== userId) {
              setIsInterviewerTyping(typingData.isTyping)

              // Auto-clear typing indicator after 10 seconds as a failsafe
              if (typingData.isTyping) {
                if (typingTimeoutRef.current) {
                  clearTimeout(typingTimeoutRef.current)
                }

                typingTimeoutRef.current = setTimeout(() => {
                  setIsInterviewerTyping(false)
                }, 10000)
              }
            }
          }
        },
        (error) => {
          console.error("Error listening for typing indicators:", error)
        }
      )

      return () => {
        typingUnsubscribe()
        cleanup()
      }
    } catch (error) {
      console.error("Error setting up typing listener:", error)
      return cleanup
    }
  }, [meetingId, userId])

  // Update typing indicator
  const updateTypingStatus = async (isTyping) => {
    if (!meetingId || !userId) return

    try {
      const typingRef = doc(db, "rooms", meetingId, "typing", "status")
      await setDoc(typingRef, {
        userId,
        isTyping,
        timestamp: serverTimestamp(),
      })
    } catch (error) {
      console.error("Error updating typing status:", error)
    }
  }

  // Handle input change and show typing indicator
  const handleInputChange = (e) => {
    setMessage(e.target.value)

    // Show typing indicator when user starts typing
    if (e.target.value.length > 0) {
      updateTypingStatus(true)
    } else {
      updateTypingStatus(false)
    }
  }

  // Send message
  const handleSendMessage = async (e) => {
    e.preventDefault()

    if (!message.trim() || !meetingId || !userId) return

    try {
      setIsLoading(true)

      // Clear typing indicator when sending a message
      await updateTypingStatus(false)

      await addDoc(collection(db, "rooms", meetingId, "messages"), {
        text: message.trim(),
        senderId: userId,
        senderName: "You",
        timestamp: serverTimestamp(),
      })

      setMessage("")
    } catch (error) {
      console.error("Error sending message:", error)
    } finally {
      setIsLoading(false)
    }
  }

  // Don't render if not open
  if (!isOpen) {
    return null
  }

  return (
    <div className="h-full w-[25%] flex flex-col font-satoshi bg-[#1a1a1a] rounded-2xl transition-all duration-300 ease-in-out ml-2 mt-2 mr-2">
      {/* Header */}
      <div className="px-6 py-4 flex justify-between items-center flex-shrink-0">
        <h3 className="text-lg font-normal text-white">In-call messages</h3>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-white transition-colors"
          title="Close"
        >
          <X size={20} />
        </button>
      </div>

      {/* Messages */}
      <div
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto px-4 py-4 space-y-4"
        style={{
          scrollbarWidth: "thin",
          scrollbarColor: "#374151 transparent",
        }}
      >
        {/* Let participants send messages toggle */}
        <div className="bg-[#252525] rounded-md px-4 py-3 flex items-center justify-between">
          <span className="text-gray-300 text-sm">
            Let participants send messages
          </span>
          <button className="relative w-11 h-6 rounded-full bg-[#4a5056] flex items-center transition-colors">
            <div className="absolute right-0.5 w-5 h-5 rounded-full bg-[#e8eaed] shadow-sm flex items-center justify-center">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-[#3c4043]"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
          </button>
        </div>

        {/* Continuous chat is OFF card */}
        <div className="bg-[#252525] rounded-xl px-3 py-4 text-center">
          <div className="flex items-center justify-center gap-2.5 mb-1">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-gray-400"
            >
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            <span className="text-gray-200 font-normal text-sm">
              Continuous chat is OFF
            </span>
          </div>
          <p className="text-gray-400 text-sm leading-relaxed text-[13px]">
            Messages won't be saved when the call ends. You can pin a message to
            make it visible for people who join later.
          </p>
        </div>

        {/* Chat Messages */}
        {messages.length === 0 ? (
          <EmptyChatState />
        ) : (
          <div>
            {messages.map((msg) => (
              <MessageBubble
                key={msg.id}
                message={msg}
                isOwnMessage={msg.senderId === userId}
              />
            ))}

            {/* Typing indicator */}
            {isInterviewerTyping && <TypingIndicator isTyping={true} />}

            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Input */}
      <div className="px-6 py-4 flex-shrink-0">
        <form onSubmit={handleSendMessage} className="relative">
          <input
            type="text"
            value={message}
            onChange={handleInputChange}
            placeholder="Send a message"
            className="w-full bg-[#1a1a1a] text-gray-100 placeholder-gray-500 rounded-full pl-5 pr-12 py-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-gray-600 border-2 border-gray-700 transition-all"
            disabled={isLoading}
          />
          <button
            type="submit"
            disabled={!message.trim() || isLoading}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-full text-gray-400 hover:text-blue-500 hover:bg-gray-800 disabled:text-gray-600 disabled:cursor-not-allowed disabled:hover:bg-transparent transition-all"
          >
            <Send size={18} />
          </button>
        </form>
      </div>
    </div>
  )
}

export default CandidateChatSidebar